/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Cell, Grid, InfoLabel, Text, VFlow } from 'bold-ui'
import { DataTable, TableColumnConfig } from 'bold-ui/lib/components/Table/DataTable/DataTable'
import { DateTime } from 'components/date'
import { createRender } from 'components/table/render'
import { Fragment } from 'react'
import { tipoGlicemiaRecord } from 'types/enums'
import { isUndefinedOrNull } from 'util/checks'
import { formatNumber } from 'util/number'
import {
  HistoricoSinaisVitaisModel,
  historicoTipoGlicemiaRecord,
  MEDICOES_SEM_GLICEMIA,
} from 'view/atendimentos/detail/historico/model/model-historico'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import HistoricoDetailTable from '../../../table/HistoricoDetailTable'
import HistoricoMedicoesDataTable from './HistoricoMedicoesDataTable'

interface HistoricoMedicoesSinaisVitaisProps {
  sinaisVitais: HistoricoSinaisVitaisModel[]
  isAtendimentoObservacao?: boolean
}

export default function HistoricoMedicoesSinaisVitais(props: Readonly<HistoricoMedicoesSinaisVitaisProps>) {
  const { sinaisVitais, isAtendimentoObservacao = false } = props

  const hasSinaisVitaisWithoutGlicemia = MEDICOES_SEM_GLICEMIA.some(
    (campo) => !isUndefinedOrNull(sinaisVitais.map((it) => it[campo]))
  )
  const hasGlicemia = sinaisVitais.some((item) => !!(item.glicemia || item.tipoGlicemia))

  return (
    <Fragment>
      {isAtendimentoObservacao ? (
        <VFlow>
          {hasSinaisVitaisWithoutGlicemia && (
            <HistoricoMedicoesDataTable titulo='Sinais vitais'>
              <DataTable<HistoricoMedicaoModel> columns={sinaisVitaisColumns} rows={sinaisVitais} />
            </HistoricoMedicoesDataTable>
          )}
          {hasGlicemia && (
            <HistoricoMedicoesDataTable titulo='Glicemia capilar'>
              <DataTable<HistoricoMedicaoModel> columns={glicemiaColumns} rows={sinaisVitais} />
            </HistoricoMedicoesDataTable>
          )}
        </VFlow>
      ) : (
        <HistoricoDetailTable title='Sinais vitais e glicemia capilar'>
          {sinaisVitais.map((sinaisVitais) => (
            <VFlow key={sinaisVitais.id}>
              <Grid>
                {renderItem('Pressão arterial', sinaisVitais.valorPressaoArterial, ' mmHg')}
                {renderItem('Freq. respiratória', sinaisVitais.frequenciaRespiratoria, ' mpm')}
                {renderItem('Freq. cardíaca', sinaisVitais.frequenciaCardiaca, ' bpm')}
                {renderItem('Temperatura', sinaisVitais.temperatura, ' °C')}
                {renderItem('Saturação de O2', sinaisVitais.saturacaoO2, ' %')}
                {renderItem('Glicemia capilar', sinaisVitais.glicemia, ' mg/dL')}
                {renderItem('Momento da coleta (glicemia)', historicoTipoGlicemiaRecord[sinaisVitais.tipoGlicemia])}
              </Grid>
            </VFlow>
          ))}
        </HistoricoDetailTable>
      )}
    </Fragment>
  )
}

const sinaisVitaisColumns: TableColumnConfig<HistoricoSinaisVitaisModel>[] = [
  {
    header: 'Data da medição',
    name: 'dataMedicao',
    render: (medicao) => <DateTime value={medicao.dataMedicao} format={`DD/MM/YYYY`} />,
  },
  {
    header: 'Pressão arterial (mmHg)',
    name: 'valorPressaoArterial',
    render: (medicao) => createRender(medicao, 'valorPressaoArterial', renderDefault),
  },
  {
    header: 'Freq. respiratória (mpm)',
    name: 'frequenciaRespiratoria',
    render: (medicao) => createRender(medicao, 'frequenciaRespiratoria', renderDefault),
  },
  {
    header: 'Freq. cardíaca (bpm)',
    name: 'frequenciaCardiaca',
    render: (medicao) => createRender(medicao, 'frequenciaCardiaca', renderDefault),
  },
  {
    header: 'Temperatura (°C)',
    name: 'temperatura',
    render: (medicao) => createRender(medicao, 'temperatura', (value) => formatNumber(value, 1)),
  },
  {
    header: 'Saturação de O2 (%)',
    name: 'saturacaoO2',
    render: (medicao) => createRender(medicao, 'saturacaoO2', renderDefault),
  },
]

const glicemiaColumns: TableColumnConfig<HistoricoSinaisVitaisModel>[] = [
  {
    header: 'Data da medição',
    name: 'dataMedicao',
    render: (medicao) => <DateTime value={medicao.dataMedicao} format={`DD/MM/YYYY`} />,
  },
  {
    header: 'Glicemia capilar (mg/dL)',
    name: 'glicemia',
    render: (medicao) => createRender(medicao, 'glicemia', renderDefault),
  },
  {
    header: 'Momento da coleta',
    name: 'tipoGlicemia',
    render: (medicao) => createRender(medicao, 'tipoGlicemia', (value) => <Text>{tipoGlicemiaRecord[value]}</Text>),
  },
]

const renderDefault = (value: string | number) => <Text>{value}</Text>

const renderItem = (titulo: string, item: string | number | undefined, unidade?: string) => {
  const hasItem = !isUndefinedOrNull(item)
  return (
    hasItem && (
      <Cell size={2}>
        <InfoLabel title={titulo}>
          {item}
          {unidade}
        </InfoLabel>
      </Cell>
    )
  )
}
